/**
 * Live accent preview while a status color is being picked.
 *
 * The row is rendered with `.status-tone`, and `--status-rgb` comes from the server stylesheet keyed
 * by `data-status-tone`. Without this, the color toggle's square next to the name keeps showing the
 * previously saved color until the form is submitted - so the choice would be made blind, which is
 * exactly what a color preview exists to prevent. Setting the property on the row overrides the
 * stylesheet rule for that row only; a reload or a discarded edit brings the stored value back.
 *
 * This module is the only writer of inline `--status-rgb` on a status row.
 *
 * Bound to the document rather than to the list: every status write replaces the whole section, and a
 * listener on the list would go with it.
 */
export function initStatusColorPreview(root) {
    if (typeof root?.addEventListener !== 'function') {
        return;
    }

    root.addEventListener('input', (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'color') {
            return;
        }

        const row = input.closest('[data-status-row]');
        const channels = hexToRgbChannels(input.value);
        if (row instanceof HTMLElement && channels !== null) {
            row.style.setProperty('--status-rgb', channels);
        }
    });
}

/**
 * @returns {string|null} `"r, g, b"` for a canonical `#rrggbb` value, null for anything else
 */
export function hexToRgbChannels(value) {
    const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(value ?? '').trim());
    if (match === null) {
        return null;
    }

    return [match[1], match[2], match[3]]
        .map((channel) => Number.parseInt(channel, 16))
        .join(', ');
}
